import { decode } from "cbor-x";

interface CoseKey {
	1: number; // kty (key type): 1=OKP, 2=EC, 3=RSA
	3: number; // alg (algorithm)
	[-1]: ArrayBuffer; // EC/OKP: curve identifier; RSA: n (modulus)
	[-2]: ArrayBuffer; // EC/OKP: x coordinate; RSA: e (exponent)
	[-3]: ArrayBuffer; // EC: y coordinate
}

function toBase64Url(buf: ArrayBuffer): string {
	return new Uint8Array(buf).toBase64({
		alphabet: "base64url",
		omitPadding: true,
	});
}

function importEc(coseKey: CoseKey): Promise<CryptoKey> {
	return crypto.subtle.importKey(
		"jwk",
		{
			kty: "EC",
			crv: "P-256",
			x: toBase64Url(coseKey[-2]),
			y: toBase64Url(coseKey[-3]),
		},
		{ name: "ECDSA", namedCurve: "P-256" },
		true,
		["verify"],
	);
}

function importRsa(coseKey: CoseKey): Promise<CryptoKey> {
	return crypto.subtle.importKey(
		"jwk",
		{
			kty: "RSA",
			n: toBase64Url(coseKey[-1]),
			e: toBase64Url(coseKey[-2]),
		},
		{ name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
		true,
		["verify"],
	);
}

function importEdDsa(coseKey: CoseKey): Promise<CryptoKey> {
	return crypto.subtle.importKey(
		"jwk",
		{
			kty: "OKP",
			crv: "Ed25519",
			x: toBase64Url(coseKey[-2]),
		},
		{ name: "Ed25519" },
		true,
		["verify"],
	);
}

export async function extractPublicKey(
	credential: PublicKeyCredential,
): Promise<ArrayBuffer> {
	const response = credential.response as AuthenticatorAttestationResponse;

	const attestationObject = decode(
		new Uint8Array(response.attestationObject),
	) as { fmt: string; authData: Uint8Array; attStmt: unknown };

	const authData = new Uint8Array(attestationObject.authData);

	const flags = authData[32];
	const hasAttestedCredData = (flags & 0x40) !== 0;

	if (!hasAttestedCredData) {
		throw new Error("No attested credential data present");
	}

	// Parse out the COSE key
	let offset = 32 + 1 + 4 + 16; // rpIdHash + flags + counter + aaguid
	const credIdLen = (authData[offset] << 8) | authData[offset + 1];
	offset += 2 + credIdLen;

	const coseKey = decode(authData.slice(offset)) as CoseKey;
	const alg = coseKey[3];

	let key: CryptoKey;
	switch (alg) {
		case -7: // ES256
			key = await importEc(coseKey);
			break;
		case -257: // RS256
			key = await importRsa(coseKey);
			break;
		case -8: // EdDSA (Ed25519)
			key = await importEdDsa(coseKey);
			break;
		default:
			throw new Error(`Unsupported algorithm: ${alg}`);
	}

	return crypto.subtle.exportKey("spki", key);
}
